import { useStaggeredAnimation } from "@/hooks/useScrollAnimation";
import { Users, Heart, Mountain, GraduationCap } from "lucide-react";

const useCases = [
  {
    icon: Users,
    title: "Youth Summer Camps",
    description: "Feed hundreds of campers with automated menu planning and Sysco ordering.",
    image: "https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&w=600&q=80"
  },
  {
    icon: Heart,
    title: "Faith-Based Retreats",
    description: "Coordinate volunteers and eliminate food waste across weekend retreats.",
    image: "https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?auto=format&fit=crop&w=600&q=80"
  },
  {
    icon: Mountain,
    title: "Outdoor Adventure Programs",
    description: "Manage expedition meals with limited storage and transport logistics.",
    image: "https://images.unsplash.com/photo-1478131143081-80f7f84ca84d?auto=format&fit=crop&w=600&q=80"
  },
  {
    icon: GraduationCap,
    title: "School & Nonprofit Camps",
    description: "Create balanced menus aligned with nutrition guidelines and budgets.",
    image: "https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?auto=format&fit=crop&w=800&q=80"
  }
];

export const UseCases = () => {
  const { containerRef, visibleItems } = useStaggeredAnimation(useCases.length, 300);

  return (
    <section id="use-cases" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-sand via-ivory to-sand/50">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="font-playfair text-4xl md:text-5xl font-bold text-slate mb-4">
            Built for Camp Kitchens of Every Size
          </h2>
          <p className="font-inter text-lg text-slate/80 max-w-3xl mx-auto">
            From youth camps to retreat centers, CampChef Pro simplifies planning, inventory, and Sysco ordering.
          </p>
        </div>

        {/* Use Cases Compact Grid */}
        <div ref={containerRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {useCases.map((useCase, index) => (
            <div
              key={index}
              className={`group relative bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-700 hover:shadow-2xl hover:-translate-y-2 ${
                visibleItems.has(index)
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-12'
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={useCase.image} 
                  alt={useCase.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-midnight-green/80 to-transparent"></div>
                
                {/* Icon Overlay */}
                <div className="absolute top-4 right-4 bg-copper/90 rounded-full p-3 shadow-lg">
                  <useCase.icon className="h-6 w-6 text-ivory" />
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="font-playfair text-xl font-bold text-slate mb-3">
                  {useCase.title}
                </h3>
                <p className="font-inter text-sm text-slate/80 leading-relaxed">
                  {useCase.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
